import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ShoppingCart,
  Loader2,
  RefreshCw,
  Search,
  CheckCircle2,
  Clock,
  AlertTriangle,
  ScrollText,
  KeyRound,
  Copy,
} from 'lucide-react';
import { extractErrorMessage } from '@/lib/api-errors';

const STATUS_BADGE = {
  premium_activated: { label: 'Premium Ativado', cls: 'bg-green-500/20 text-green-400 border border-green-500/40', icon: CheckCircle2 },
  pending_user: { label: 'Aguarda Registo', cls: 'bg-gold/20 text-gold border border-gold/40', icon: Clock },
  failed_no_email: { label: 'Falhou (sem email)', cls: 'bg-red-500/20 text-red-400 border border-red-500/40', icon: AlertTriangle },
};

const LEVEL_BADGE = {
  info: 'bg-blue-500/20 text-blue-300 border border-blue-500/30',
  warning: 'bg-gold/20 text-gold border border-gold/40',
  error: 'bg-red-500/20 text-red-400 border border-red-500/40',
};

function fmtDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('pt-PT', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return value;
  }
}

export default function AdminWooCommerce() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState('all');
  const [emailFilter, setEmailFilter] = useState('');
  const [logLevelFilter, setLogLevelFilter] = useState('all');
  const [retryingKey, setRetryingKey] = useState(null);

  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL || ''}/api/webhooks/woocommerce`;

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, purchasesRes, logsRes] = await Promise.all([
        api.get('/admin/woocommerce/stats'),
        api.get('/admin/woocommerce/purchases', {
          params: {
            limit: 200,
            ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
            ...(emailFilter ? { email: emailFilter } : {}),
          },
        }),
        api.get('/admin/woocommerce/logs', {
          params: {
            limit: 100,
            ...(logLevelFilter !== 'all' ? { level: logLevelFilter } : {}),
          },
        }),
      ]);
      setStats(statsRes.data);
      setPurchases(purchasesRes.data.items || []);
      setLogs(logsRes.data.items || []);
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao carregar dados'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, emailFilter, logLevelFilter]);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    loadAll();
  }, [user, navigate, loadAll]);

  const retry = async (purchase) => {
    const key = `${purchase.order_id}-${purchase.product_id}`;
    setRetryingKey(key);
    try {
      await api.post(`/admin/woocommerce/purchases/${purchase.order_id}/${purchase.product_id}/retry`);
      toast.success('Premium ativado');
      loadAll();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao tentar novamente'));
    } finally {
      setRetryingKey(null);
    }
  };

  const copy = (value) => {
    try {
      navigator.clipboard.writeText(value);
      toast.success('Copiado');
    } catch {
      // ignore
    }
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-ruby" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="max-w-6xl mx-auto px-4 pt-24 pb-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin')}
              className="text-muted-foreground hover:text-foreground"
              data-testid="woo-back"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-ruby/20 flex items-center justify-center">
                <ShoppingCart className="w-6 h-6 text-ruby" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">WooCommerce — Compras Premium</h1>
                <p className="text-muted-foreground">
                  Histórico, webhooks e ativação de Premium via pimentadocelingerie.pt
                </p>
              </div>
            </div>
          </div>
          <Button onClick={loadAll} variant="outline" className="border-border" data-testid="woo-refresh">
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatBox label="Premium Ativado" value={stats?.premium_activated ?? 0} color="green-400" />
          <StatBox label="Aguarda Registo" value={stats?.pending_user ?? 0} color="gold" />
          <StatBox label="Falhas" value={stats?.failed ?? 0} color="red-400" />
          <StatBox label="Total Logs" value={stats?.total_logs ?? 0} color="ruby" />
        </div>

        {/* Webhook config card */}
        <Card className="bg-surface border-border mb-6">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-ruby" />
              Configuração do Webhook
            </CardTitle>
            <CardDescription>
              Cola estes valores em <strong>WooCommerce → Definições → Avançado → Webhooks</strong> ao criar o webhook.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground w-32 shrink-0">Delivery URL</span>
              <code className="flex-1 bg-background border border-border rounded-md px-3 py-2 text-sm font-mono break-all" data-testid="woo-webhook-url">
                {webhookUrl}
              </code>
              <Button size="sm" variant="outline" onClick={() => copy(webhookUrl)} className="border-border">
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground w-32 shrink-0">Topic</span>
              <code className="flex-1 bg-background border border-border rounded-md px-3 py-2 text-sm font-mono">order.updated</code>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground w-32 shrink-0">API Version</span>
              <code className="flex-1 bg-background border border-border rounded-md px-3 py-2 text-sm font-mono">WP REST API Integration v3</code>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-xs text-muted-foreground w-32 shrink-0 pt-2">Secret</span>
              <div className="flex-1">
                <div className="bg-background border border-border rounded-md px-3 py-2 text-sm">
                  {stats?.secret_configured ? (
                    <span className="text-green-400 inline-flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" /> Configurado no servidor
                    </span>
                  ) : (
                    <span className="text-red-400 inline-flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" /> Não configurado — define <code>WOOCOMMERCE_WEBHOOK_SECRET</code>
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  O mesmo secret deve ser colado no campo "Secret" do WooCommerce.
                </p>
              </div>
            </div>
            <div className="pt-3 border-t border-border">
              <p className="text-xs text-muted-foreground mb-2">Mapeamento de produtos:</p>
              <div className="flex flex-wrap gap-2">
                {stats?.product_map && Object.entries(stats.product_map).map(([pid, info]) => (
                  <Badge key={pid} variant="outline" className="border-border font-mono">
                    #{pid} → {info.label} ({info.duration_days}d)
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="purchases" className="w-full">
          <TabsList className="bg-surface border border-border">
            <TabsTrigger value="purchases" data-testid="tab-purchases">Compras</TabsTrigger>
            <TabsTrigger value="logs" data-testid="tab-logs">
              <ScrollText className="w-4 h-4 mr-1" /> Logs
            </TabsTrigger>
          </TabsList>

          {/* Purchases tab */}
          <TabsContent value="purchases">
            <Card className="bg-surface border-border">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                  <div className="flex-1 space-y-1">
                    <CardTitle className="text-foreground">Histórico de subscrições Premium</CardTitle>
                    <CardDescription>
                      Inclui compras via Pimenta Doce Lingerie e subscrições adicionadas manualmente pela administração.
                    </CardDescription>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Filtrar por email…"
                        value={emailFilter}
                        onChange={(e) => setEmailFilter(e.target.value)}
                        className="pl-9 bg-background border-border w-full sm:w-[220px]"
                        data-testid="woo-email-filter"
                      />
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="bg-background border-border w-full sm:w-[200px]" data-testid="woo-status-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos os estados</SelectItem>
                        <SelectItem value="premium_activated">Premium Ativado</SelectItem>
                        <SelectItem value="pending_user">Aguarda Registo</SelectItem>
                        <SelectItem value="failed_no_email">Falhou</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {purchases.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-12 text-center">
                    Ainda não há compras registadas.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Email</TableHead>
                          <TableHead>Order ID</TableHead>
                          <TableHead>Produto</TableHead>
                          <TableHead>Premium</TableHead>
                          <TableHead>Ativação</TableHead>
                          <TableHead>Expira</TableHead>
                          <TableHead>Estado</TableHead>
                          <TableHead className="text-right">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {purchases.map((p) => {
                          const status = STATUS_BADGE[p.status] || { label: p.status, cls: 'bg-muted text-muted-foreground', icon: AlertTriangle };
                          const Icon = status.icon;
                          const isManual = p.source === 'manual';
                          return (
                            <TableRow key={`${p.order_id}-${p.product_id || 'manual'}`} data-testid={`woo-purchase-${p.order_id}-${p.product_id || 'manual'}`}>
                              <TableCell className="font-medium text-foreground">
                                <div className="flex items-center gap-2">
                                  {p.email || '—'}
                                  {isManual && (
                                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-ruby/15 text-ruby">
                                      Manual
                                    </span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="font-mono text-xs">
                                {isManual ? <span className="text-muted-foreground">{p.order_id}</span> : `#${p.order_id}`}
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-col">
                                  <span className="text-sm text-foreground">{p.product_label || p.label || 'Premium'}</span>
                                  {p.product_id && (
                                    <span className="text-xs text-muted-foreground font-mono">#{p.product_id}</span>
                                  )}
                                  {isManual && p.granted_by && (
                                    <span className="text-[11px] text-muted-foreground">por {p.granted_by}</span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>{p.duration_days || '—'} dias</TableCell>
                              <TableCell className="text-xs">{fmtDate(p.activated_at || p.premium_starts_at || p.created_at)}</TableCell>
                              <TableCell className="text-xs">{fmtDate(p.premium_until || p.premium_expires_at)}</TableCell>
                              <TableCell>
                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs ${status.cls}`}>
                                  <Icon className="w-3 h-3" />
                                  {status.label}
                                </span>
                                {p.error && (
                                  <p className="text-xs text-red-400 mt-1 max-w-xs">{p.error}</p>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                {p.status === 'pending_user' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => retry(p)}
                                    disabled={retryingKey === `${p.order_id}-${p.product_id}`}
                                    className="border-border"
                                    data-testid={`woo-retry-${p.order_id}-${p.product_id}`}
                                  >
                                    {retryingKey === `${p.order_id}-${p.product_id}` ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      'Tentar ativar'
                                    )}
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Logs tab */}
          <TabsContent value="logs">
            <Card className="bg-surface border-border">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                  <div className="flex-1 space-y-1">
                    <CardTitle className="text-foreground">Logs do webhook</CardTitle>
                    <CardDescription>
                      Todas as chamadas recebidas (válidas, recusadas, ignoradas, erros).
                    </CardDescription>
                  </div>
                  <Select value={logLevelFilter} onValueChange={setLogLevelFilter}>
                    <SelectTrigger className="bg-background border-border w-full sm:w-[180px]" data-testid="woo-log-level">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os níveis</SelectItem>
                      <SelectItem value="info">info</SelectItem>
                      <SelectItem value="warning">warning</SelectItem>
                      <SelectItem value="error">error</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                {logs.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-12 text-center">Sem logs.</p>
                ) : (
                  <div className="space-y-2">
                    {logs.map((log) => (
                      <div
                        key={log.id}
                        className="p-3 bg-background border border-border rounded-lg text-sm"
                        data-testid={`woo-log-${log.id}`}
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-xs font-mono px-2 py-0.5 rounded ${LEVEL_BADGE[log.level] || 'bg-muted text-muted-foreground'}`}>
                            {log.level}
                          </span>
                          <span className="font-medium text-foreground">{log.event}</span>
                          {log.order_id && (
                            <span className="text-xs font-mono text-muted-foreground">#order {log.order_id}</span>
                          )}
                          {log.signature_valid === false && (
                            <Badge className="bg-red-500/20 text-red-400 border border-red-500/40">Assinatura inválida</Badge>
                          )}
                          <span className="text-xs text-muted-foreground ml-auto">{fmtDate(log.created_at)}</span>
                        </div>
                        {log.error && (
                          <p className="text-xs text-red-400 mt-1">{log.error}</p>
                        )}
                        {log.payload_summary && (
                          <pre className="text-xs text-muted-foreground bg-surface p-2 rounded mt-2 overflow-x-auto whitespace-pre-wrap">
{JSON.stringify(log.payload_summary, null, 2)}
                          </pre>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function StatBox({ label, value, color = 'ruby' }) {
  return (
    <Card className="bg-surface border-border">
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className={`text-2xl font-bold text-${color}`}>{value}</p>
      </CardContent>
    </Card>
  );
}
